// Site-wide content / mock data for inkaa.space

export const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services" },
  { label: "Work", to: "/work" },
  { label: "Journal", to: "/journal" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export const SERVICES = [
  {
    id: "web",
    number: "01",
    title: "Web Development",
    tagline: "Production-grade. Stupid fast. Pixel-honest.",
    description:
      "Bespoke websites and web apps built on modern stacks (Next.js, React, Node, headless CMS). Server-rendered, accessible, SEO-ready and benchmarked for sub-second loads.",
    deliverables: [
      "Marketing & SaaS websites",
      "Headless CMS integrations",
      "Web apps & dashboards",
      "E-commerce experiences",
      "API-first backends",
      "Performance audits",
    ],
    stack: ["Next.js", "React", "FastAPI", "Sanity", "Shopify", "PostgreSQL"],
  },
  {
    id: "marketing",
    number: "02",
    title: "Marketing & Brand",
    tagline: "Story-first. Data-loyal. Impossible to ignore.",
    description:
      "Full-funnel performance marketing: positioning, paid media, content, and creative. We build engines that compound — not campaigns that vanish.",
    deliverables: [
      "Performance ads (Meta / Google)",
      "Content & editorial",
      "Email & lifecycle",
      "Brand systems",
      "Creative production",
      "Attribution & analytics",
    ],
    stack: ["GA4", "GTM", "HubSpot", "Klaviyo", "Webflow", "Notion"],
  },
  {
    id: "seo",
    number: "03",
    title: "SEO",
    tagline: "Rank where buyers search. Own the click.",
    description:
      "Technical SEO + topical authority + link earning. We engineer organic compounding so you stop renting traffic and start owning it.",
    deliverables: [
      "Technical audits",
      "Site architecture",
      "Programmatic SEO",
      "Content clusters",
      "Schema & structured data",
      "Authority building",
    ],
    stack: ["Ahrefs", "Search Console", "Screaming Frog", "Semrush", "Sitebulb"],
  },
  {
    id: "aeo",
    number: "04",
    title: "AEO — Answer Engine Optimization",
    tagline: "Be the answer in zero-click search.",
    description:
      "Optimize for featured snippets, People Also Ask, voice and assistants. We architect content for direct extraction so your brand becomes the answer.",
    deliverables: [
      "Snippet engineering",
      "Q&A topology",
      "Schema for answers",
      "Voice search prep",
      "Knowledge graph hygiene",
      "Entity SEO",
    ],
    stack: ["Schema.org", "Wikidata", "FAQPage", "Speakable", "AlsoAsked"],
  },
  {
    id: "geo",
    number: "05",
    title: "GEO — Generative Engine Optimization",
    tagline: "Get cited inside ChatGPT, Perplexity & Gemini.",
    description:
      "When buyers ask AI, your brand should be in the answer. We build citation-worthy content, structured data and authority signals for generative search.",
    deliverables: [
      "LLM citation strategy",
      "Source authority mapping",
      "Generative content briefs",
      "Brand entity reinforcement",
      "Prompt-pattern research",
      "AI traffic analytics",
    ],
    stack: ["Perplexity", "ChatGPT", "Gemini", "Bing Copilot", "Otterly.ai"],
  },
];

export const STATS = [
  { value: "+312%", label: "Avg. organic growth, year one" },
  { value: "<0.9s", label: "Median LCP across launches" },
  { value: "47", label: "Brands shipped to production" },
  { value: "11", label: "Countries served" },
];

export const PROJECTS = [
  {
    id: "northwave",
    name: "Northwave Capital",
    sector: "Fintech",
    year: "2025",
    blurb: "Rebuilt the marketing system. 4.2x lift in inbound qualified leads in 90 days.",
    image:
      "https://images.unsplash.com/photo-1634084462412-b54873c0a56d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjB3ZWIlMjBkZXNpZ24lMjBhZ2VuY3klMjBwb3J0Zm9saW8lMjBzaG93Y2FzZXxlbnwwfHx8fDE3NzcxODM3NDl8MA&ixlib=rb-4.1.0&q=80&w=1000",
    span: "lg:col-span-8",
    tags: ["Web", "Brand", "SEO"],
  },
  {
    id: "altura",
    name: "Altura Living",
    sector: "Real Estate",
    year: "2025",
    blurb: "Top 3 organic ranking on 14 metro keywords in 6 months.",
    image:
      "https://images.pexels.com/photos/8408538/pexels-photo-8408538.jpeg?auto=compress&cs=tinysrgb&h=650&w=800",
    span: "lg:col-span-4",
    tags: ["SEO", "AEO"],
  },
  {
    id: "kintsu",
    name: "Kintsu Studio",
    sector: "DTC Skincare",
    year: "2024",
    blurb: "Storefront + ad creative engine. 2.7 ROAS sustained at scale.",
    image:
      "https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=1200&q=80&auto=format&fit=crop",
    span: "lg:col-span-4",
    tags: ["E-commerce", "Marketing"],
  },
  {
    id: "halonet",
    name: "Halonet AI",
    sector: "AI SaaS",
    year: "2025",
    blurb: "Cited in ChatGPT for 38 buyer-intent prompts within 90 days.",
    image:
      "https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=1200&q=80&auto=format&fit=crop",
    span: "lg:col-span-8",
    tags: ["GEO", "Web"],
  },
  {
    id: "voltline",
    name: "Voltline Mobility",
    sector: "EV / Hardware",
    year: "2024",
    blurb: "Launched D2C site, scaled paid media, hit 9-figure inbound pipeline.",
    image:
      "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=1200&q=80&auto=format&fit=crop",
    span: "lg:col-span-6",
    tags: ["Web", "Marketing"],
  },
  {
    id: "atlasmd",
    name: "Atlas MD",
    sector: "Healthtech",
    year: "2025",
    blurb: "HIPAA-grade portal + AEO. Reduced CAC by 41%.",
    image:
      "https://images.unsplash.com/photo-1551434678-e076c223a692?w=1200&q=80&auto=format&fit=crop",
    span: "lg:col-span-6",
    tags: ["Web", "AEO"],
  },
];

export const PROCESS = [
  {
    step: "01",
    title: "Discover",
    body: "We sit with your data, your audience, your unfair advantages. No briefs. No fluff.",
  },
  {
    step: "02",
    title: "Architect",
    body: "Strategy, information architecture, brand system, and a measurable success contract.",
  },
  {
    step: "03",
    title: "Build",
    body: "Production code, content, creative — shipped in weekly sprints with full transparency.",
  },
  {
    step: "04",
    title: "Compound",
    body: "We run the engine, double down on what works, and engineer growth that compounds.",
  },
];

export const TESTIMONIALS = [
  {
    quote:
      "Inkaa rebuilt our website and our SEO from zero. We now get more inbound in a week than we used to in a quarter.",
    name: "Aarav Mehta",
    role: "CEO, Northwave Capital",
  },
  {
    quote:
      "These guys obsess over the work the way you wish your in-house team did. Performance is genuinely best-in-class.",
    name: "Lena Park",
    role: "Head of Growth, Halonet AI",
  },
  {
    quote:
      "Got us cited inside ChatGPT for our category. That alone paid for the engagement four times over.",
    name: "Marcus Diallo",
    role: "Founder, Kintsu Studio",
  },
];

export const CAPABILITIES = [
  "Next.js / React",
  "FastAPI / Node",
  "Headless CMS",
  "Webflow",
  "Shopify",
  "Framer",
  "Figma",
  "Motion design",
  "GA4 / GTM",
  "Google Ads",
  "Meta Ads",
  "Klaviyo",
  "HubSpot",
  "Schema / SD",
  "Programmatic SEO",
  "Perplexity GEO",
  "ChatGPT GEO",
  "Edge & CDN",
];

export const SERVICE_OPTIONS = [
  "Web Development",
  "Marketing & Brand",
  "SEO",
  "AEO",
  "GEO",
  "All of the above",
];

export const BUDGET_OPTIONS = [
  "₹50k – ₹2L",
  "₹2L – ₹10L",
  "₹10L – ₹40L",
  "₹40L – ₹1Cr",
  "₹1Cr+",
  "Not sure yet",
];

export const FAQS = [
  {
    category: "General",
    questions: [
      {
        q: "What makes Inkaa different from a standard digital agency?",
        a: "We are a senior-only studio. No accounts managers, no junior hand-offs. You work directly with the people building your product. We optimize for high-conviction metrics like LCP and citation authority, not just vanity traffic.",
      },
      {
        q: "How fast can you launch a project in 2026?",
        a: "A typical high-performance website takes 4–8 weeks from discovery to deployment. Complex SaaS platforms or programmatic SEO engines may take 10–12 weeks.",
      },
    ],
  },
  {
    category: "Web Development",
    questions: [
      {
        q: "Do you use templates or builders?",
        a: "Never. Everything we build is bespoke, using modern stacks like Next.js and React. This ensures sub-second load times and a 100/100 Lighthouse performance score which is essential for 2026 rankings.",
      },
      {
        q: "How do you ensure a website is 'Future-Proof'?",
        a: "We architect for the 'Edge.' This means your site is distributed globally, loads instantly, and is structured for both human users and AI crawlers simultaneously.",
      },
    ],
  },
  {
    category: "SEO & AEO",
    questions: [
      {
        q: "What is AEO and why do I need it in 2026?",
        a: "Answer Engine Optimization (AEO) is the practice of structuring your content to be the definitive answer for voice search and featured snippets. With 'zero-click' searches rising, AEO ensures you own the real estate at the very top of the SERP.",
      },
      {
        q: "How long until we see SEO results?",
        a: "Technical fixes show impact within weeks. Topical authority and ranking for high-intent keywords typically take 3–6 months of compounding effort.",
      },
    ],
  },
  {
    category: "GEO (AI Search)",
    questions: [
      {
        q: "How do you get a brand cited inside ChatGPT and Perplexity?",
        a: "Generative Engine Optimization (GEO) involves building source authority and entity-based content that AI models trust. We use specific schema markups and citation-frequency strategies to ensure your brand is the one being recommended by LLMs.",
      },
      {
        q: "Is GEO just the new SEO?",
        a: "It's different. SEO is about links and keywords; GEO is about semantic relevance and authority signaling. In 2026, you need both to stay visible.",
      },
    ],
  },
];

export const POSTS = [
  {
    id: "geo-playbook-2026",
    title: "The GEO Playbook: How to win in the Perplexity era",
    date: "Apr 27, 2026",
    readTime: "12 min read",
    category: "Generative SEO",
    image: "https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=1000&q=80",
    excerpt: "Why traditional keyword targeting is dying and how semantic entity reinforcement is the only way to stay cited in AI search engines like Perplexity, Gemini, and ChatGPT.",
    content: `
      <p class="lead">The transition from Search Engine Optimization (SEO) to Generative Engine Optimization (GEO) is the single biggest shift in digital marketing since the mobile-first index. In 2026, you don't rank for keywords; you rank for trust.</p>
      
      <h2>The Death of the Keyword</h2>
      <p>Traditional SEO was built on a simple premise: match the user's string of characters. But LLMs (Large Language Models) don't care about strings. They care about <strong>concepts and entities</strong>. When a user asks Perplexity, "What's the best high-performance agency in India?", the model isn't looking for the keyword "best high-performance agency India"—it's looking for a brand that has the highest density of citations across trusted developer forums, case study platforms, and technical benchmarks.</p>
      
      <blockquote class="border-l-4 border-[#FF4500] pl-6 py-2 my-8 italic text-white/90 bg-white/5 rounded-r-xl">
        "In the age of AI search, your website is no longer a destination; it's a training set."
      </blockquote>

      <h2>The Three Pillars of GEO</h2>
      <h3>1. Entity Reinforcement</h3>
      <p>Consistently linking your brand to specific expertise categories. We use JSON-LD Schema to explicitly define your business as a 'ProfessionalService' with specific 'knowsAbout' properties. This eliminates ambiguity for AI crawlers.</p>
      
      <h3>2. Citation Frequency</h3>
      <p>Generative engines rely on consensus. If your brand is mentioned on Reddit, GitHub, and niche industry blogs as the answer to a specific problem, the AI's 'confidence score' in your brand increases. We call this <em>Consensus Engineering</em>.</p>
      
      <h3>3. Semantic Content Structures</h3>
      <p>Content must be architected for extraction. Use clear Q&A headings, bulleted lists for technical specs, and definitive summaries. If a bot can't summarize your page in 3 sentences, it won't cite it.</p>

      <div class="bg-[#CCFF00]/10 border border-[#CCFF00]/20 p-6 rounded-2xl my-10">
        <h4 class="text-[#CCFF00] font-bold mb-2 uppercase text-xs tracking-widest">Savage Tip</h4>
        <p class="text-sm text-white/80">Stop writing 'How-to' guides for humans only. Start writing 'Fact Sheets' for machines. AI prefers structured data and verifiable claims over poetic prose.</p>
      </div>

      <h2>Conclusion</h2>
      <p>The brands that win in 2026 will be those that stop renting traffic from Google Ads and start owning the 'Answer Space' through technical authority and semantic clarity.</p>
    `
  },
  {
    id: "performance-as-a-feature",
    title: "Performance as a Feature: Why 0.8s LCP is the new baseline",
    date: "Apr 22, 2026",
    readTime: "9 min read",
    category: "Engineering",
    image: "https://images.unsplash.com/photo-1551434678-e076c223a692?w=1000&q=80",
    excerpt: "Speed isn't a luxury; it's a conversion multiplier. Here's how we architect React apps for sub-second perception and 100/100 Lighthouse scores.",
    content: `
      <p>In 2026, user patience is non-existent. If your site takes more than 1 second to feel interactive, you've already lost the conversion. But speed is more than just a metric; it's a <strong>psychological anchor</strong>.</p>
      
      <h2>The Psychology of Instant</h2>
      <p>Studies show that perceived performance is more important than raw load time. When a site loads instantly, the user subconsciously attributes qualities of efficiency, reliability, and modernism to the brand. Conversely, a slow site creates 'Micro-Friction'—a subtle frustration that compounds into a high bounce rate.</p>

      <h2>Our Sub-0.9s Blueprint</h2>
      <ul>
        <li><strong>Resource Preloading:</strong> We force the browser to download critical fonts and hero assets before it even finishes parsing the CSS.</li>
        <li><strong>CSS-Only Hero Reveals:</strong> By using native CSS keyframes for our first animations, we bypass the 1.1s delay caused by JavaScript execution.</li>
        <li><strong>Granular Code Splitting:</strong> We divide the app into dozens of small chunks. The browser only downloads exactly what is needed for the current view.</li>
      </ul>

      <p>At Inkaa, we don't just build websites; we build high-frequency trading platforms for your brand's message. Every millisecond we shave off is a dollar we put back into your marketing budget.</p>
    `
  },
  {
    id: "branding-for-conviction",
    title: "Engineering Conviction: The psychology of next-gen brand systems",
    date: "Apr 15, 2026",
    readTime: "10 min read",
    category: "Design",
    image: "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=1000&q=80",
    excerpt: "Visual design is the interface for trust. How we build brand systems that drive high-ticket conversions in a crowded digital landscape.",
    content: `
      <p>Good design is beautiful. Great design is a psychological anchor that creates conviction in your buyer's mind. In an era where AI can generate 'pretty' images in seconds, <strong>Strategic Design</strong> is the only thing that separates the commodity from the premium.</p>
      
      <h2>Visual Trust Signals</h2>
      <p>Trust isn't built with a logo; it's built with consistency. We focus on 'High-Frequency Brand Touchpoints'—the subtle interactions that happen thousands of times. The weight of a font, the ease of a transition, the clarity of a grid. These are the signals that tell a buyer, 'This company is elite.'</p>

      <h3>The 'Inkaa' Aesthetic</h3>
      <p>We lean into high-contrast, dark-mode systems because they focus the user's attention. By using colors like <strong>Savage Orange (#FF4500)</strong> and <strong>Electric Lime (#CCFF00)</strong>, we create a visual 'shock' that breaks the user's scrolling habit and forces them to engage with the message.</p>
    `
  },
  {
    id: "death-of-the-landing-page",
    title: "The Death of the Landing Page: Why Flow is Everything",
    date: "Apr 02, 2026",
    readTime: "7 min read",
    category: "Conversion",
    image: "https://images.unsplash.com/photo-1634084462412-b54873c0a56d?w=1000&q=80",
    excerpt: "Static landing pages are becoming obsolete. In 2026, the brands that convert best are those that treat their website as a continuous, interactive flow.",
    content: `
      <p>The era of the 'one-page lander' is ending. Today's sophisticated buyer wants an experience, not a pitch. We're seeing a massive shift toward <strong>Story-Driven Interfaces</strong>.</p>
      
      <h2>Moving from Pitch to Progress</h2>
      <p>A static page is a dead end. A 'Flow' is a progression. We build interfaces that guide the user through a series of micro-commitments—from an insight to a case study to a live demo—without ever feeling like they are being 'sold' to.</p>
    `
  }
];
